/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.database

import androidx.room3.AutoMigration
import androidx.room3.ColumnTypeConverters
import androidx.room3.ConstructedBy
import androidx.room3.Database
import androidx.room3.RoomDatabase
import androidx.room3.RoomDatabaseConstructor
import kpt.core.database.config.DatabaseConfig
import kpt.core.database.config.ForkDatabaseConfig

/**
 * KSP-generated constructor bridge for [AppDatabase].
 *
 * Room 3 requires an `expect object` annotated with `@ConstructedBy` so its compiler plugin can
 * generate the platform-specific `actual object` holding the `AppDatabase_Impl` instantiation.
 */
expect object AppDatabaseConstructor : RoomDatabaseConstructor<AppDatabase> {
    override fun initialize(): AppDatabase
}

/**
 * GENERATED — do not hand-edit, and do not look for this file in `src/`.
 *
 * Emitted by `tools/database-ksp` from the `@DbEntity` / `@DbDao` / `@DbConverters`
 * annotations on the tables themselves, plus the framework infra tables
 * (`core-base/database/module-schema.yaml`) and this fork's applied migration sequence
 * (`app-profile/migration-ledger.yaml`), both passed in as KSP args.
 *
 * ## Why this file is generated whole
 * It was the LAST hand-merged file under `core/`: Room needs ONE compile-time
 * `entities = [...]` literal, so a fork's tables could not live in a separate file the way
 * every other seam works, and a template sync had to 3-way merge it. Deriving the literal
 * from the annotations removes the merge rather than arbitrating it — there is no longer a
 * committed file for two sides to disagree about.
 *
 * ## Adding a table
 * Write the `@Entity` and annotate it `@DbEntity`; write the `@Dao` and annotate it `@DbDao`.
 * That is the whole declaration — no YAML row, no accessor to add here, no Koin binding to
 * write (`GeneratedDaoBindings` comes from the same `@DbDao`). Schema HISTORY is the one
 * thing you still record by hand, in the migration ledger, because no class can carry it and
 * a version invented by scanning source strands every installed device.
 */
@Database(
    entities = [
        kpt.core.base.database.infra.entity.BookkeeperEntity::class,
        kpt.core.base.database.infra.entity.FetchedAtEntity::class,
        kpt.core.base.database.infra.entity.DraftEntity::class,
        kpt.core.base.database.infra.entity.ConflictEntity::class,
        kpt.core.database.alerts.AlertEntity::class,
        kpt.core.database.banking.entity.BillReminderEntity::class,
        kpt.core.database.banking.entity.LoanEntity::class,
        kpt.core.database.cloudtodo.CloudTodoEntity::class,
        kpt.core.database.crypto.entity.CoinDetailEntity::class,
        kpt.core.database.crypto.entity.CoinMarketEntity::class,
        kpt.core.database.currency.entity.ExchangeRatesEntity::class,
        kpt.core.database.currency.entity.RateHistoryEntity::class,
        kpt.core.database.economic.InterestRateSeriesEntity::class,
        kpt.core.database.watchlist.entity.WatchlistEntity::class,
    ],
    version = AppDatabase.VERSION,
    exportSchema = true,
    autoMigrations = [
        AutoMigration(from = 3, to = 4),
        AutoMigration(from = 4, to = 5),
        AutoMigration(from = 5, to = 6),
        AutoMigration(from = 6, to = 7),
        AutoMigration(from = 7, to = 8),
        AutoMigration(from = 8, to = 10, spec = kpt.core.database.migrations.MigrationSpec8to10::class),
        AutoMigration(from = 10, to = 11),
        AutoMigration(from = 11, to = 12),
        AutoMigration(from = 12, to = 13),
    ],
)
@ColumnTypeConverters(
    kpt.core.database.banking.converter.BankingTypeConverters::class,
    kpt.core.database.crypto.converter.FintechTypeConverters::class,
    kpt.core.database.currency.converter.ChargeTypeConverters::class,
)
@ConstructedBy(AppDatabaseConstructor::class)
abstract class AppDatabase : RoomDatabase() {

    abstract val bookkeeperDao: kpt.core.base.database.infra.dao.BookkeeperDao
    abstract val fetchedAtDao: kpt.core.base.database.infra.dao.FetchedAtDao
    abstract val draftDao: kpt.core.base.database.infra.dao.DraftDao
    abstract val conflictDao: kpt.core.base.database.infra.dao.ConflictDao

    abstract val alertDao: kpt.core.database.alerts.AlertDao
    abstract val billReminderDao: kpt.core.database.banking.dao.BillReminderDao
    abstract val cloudTodoDao: kpt.core.database.cloudtodo.CloudTodoDao
    abstract val coinDetailDao: kpt.core.database.crypto.dao.CoinDetailDao
    abstract val coinMarketDao: kpt.core.database.crypto.dao.CoinMarketDao
    abstract val exchangeRatesDao: kpt.core.database.currency.dao.ExchangeRatesDao
    abstract val interestRateSeriesDao: kpt.core.database.economic.InterestRateSeriesDao
    abstract val loanDao: kpt.core.database.banking.dao.LoanDao
    abstract val rateHistoryDao: kpt.core.database.currency.dao.RateHistoryDao
    abstract val watchlistDao: kpt.core.database.watchlist.dao.WatchlistDao

    companion object {
        /**
         * Effective Room schema version — owned OUTRIGHT by the fork ([ForkDatabaseConfig],
         * generated from `app-profile/migration-ledger.yaml#version`). The template does not
         * contribute to it; it ships `migration_units` the fork's ledger places at its own
         * next free version.
         */
        const val VERSION = ForkDatabaseConfig.VERSION

        /**
         * Fork-unique on-disk DB filename — single source of truth is [DatabaseConfig.NAME]
         * (appId-derived, regenerated by `syncForkConfig`). All platform builders read this.
         */
        const val DATABASE_NAME = DatabaseConfig.NAME
    }
}
